import { flow, makeAutoObservable, observable, runInAction } from "mobx";

import { parseSSEEventsFromBuffer } from "../utils/parsers";

import { ENDPOINTS } from "@/api/application-api";
import type { ConversationMessage } from "@/queries/vibe/useRecentVibeChatSessions";
import { jsonAuthHeaders } from "@/utils/headers";

const RECEIVING_EVENTS_IDLE_MS = 1000;
const TOOL_CALL_THINKING_IDLE_MS = 350;

export interface VibeChatRequest {
  message: string;
  conversation_history?: ConversationMessage[];
  workspace_id?: number;
  session_id?: string;
  current_url?: string;
}

export type StreamEventCallback = (line: string) => void;

export interface SendMessageHandlers {
  onEvent: StreamEventCallback;
  onNonStreamingResponse?: (result: {
    response?: string;
    message?: string;
    debug?: Record<string, unknown>;
  }) => void;
  onError?: (error: Error, assistantMessageIndex: number, lastUserMessageId: string | null) => void;
  onStreamComplete?: () => void;
}

interface ChatStreamState {
  isLoading: boolean;
  hasStreamingStarted: boolean;
  isReceivingEvents: boolean;
  showThinkingAfterToolCall: boolean;
  abortController: AbortController | null;
  receivingEventsTimeout: ReturnType<typeof setTimeout> | null;
  toolCallThinkingTimeout: ReturnType<typeof setTimeout> | null;
  onStreamCompleteCallback: (() => void) | null;
}

const createChatStreamState = (): ChatStreamState => ({
  isLoading: false,
  hasStreamingStarted: false,
  isReceivingEvents: false,
  showThinkingAfterToolCall: false,
  abortController: null,
  receivingEventsTimeout: null,
  toolCallThinkingTimeout: null,
  onStreamCompleteCallback: null,
});

export class StreamStore {
  private streams = observable.map<string, ChatStreamState>();
  private authToken: string | null = null;
  private currentWorkspaceId: number | null = null;

  constructor() {
    // Reader methods must stay plain functions: makeAutoObservable would wrap
    // them as actions, and MobX actions run untracked, which breaks observer
    // re-renders that read stream state through them.
    makeAutoObservable(this, {
      isLoadingFor: false,
      hasStreamingStartedFor: false,
      isReceivingEventsFor: false,
      showThinkingAfterToolCallFor: false,
      getActiveStreamChatIds: false,
      getCurrentWorkspaceId: false,
    });
  }

  private getOrCreate(chatId: string): ChatStreamState {
    let state = this.streams.get(chatId);
    if (!state) {
      state = observable(createChatStreamState());
      this.streams.set(chatId, state);
    }
    return state;
  }

  setAuthToken(token: string | null) {
    this.authToken = token;
  }

  getCurrentWorkspaceId(): number | null {
    return this.currentWorkspaceId;
  }

  setCurrentWorkspaceId(workspaceId: number | null) {
    this.currentWorkspaceId = workspaceId;
  }

  get isAnyLoading(): boolean {
    for (const state of this.streams.values()) {
      if (state.isLoading) {
        return true;
      }
    }
    return false;
  }

  getActiveStreamChatIds(): string[] {
    const chatIds: string[] = [];
    this.streams.forEach((state, chatId) => {
      if (state.isLoading) {
        chatIds.push(chatId);
      }
    });
    return chatIds;
  }

  isLoadingFor(chatId: string): boolean {
    return this.streams.get(chatId)?.isLoading ?? false;
  }

  hasStreamingStartedFor(chatId: string): boolean {
    return this.streams.get(chatId)?.hasStreamingStarted ?? false;
  }

  isReceivingEventsFor(chatId: string): boolean {
    return this.streams.get(chatId)?.isReceivingEvents ?? false;
  }

  showThinkingAfterToolCallFor(chatId: string): boolean {
    return this.streams.get(chatId)?.showThinkingAfterToolCall ?? false;
  }

  cleanup(chatId: string) {
    const state = this.streams.get(chatId);
    if (!state) {
      return;
    }

    state.isLoading = false;
    state.hasStreamingStarted = false;
    state.isReceivingEvents = false;
    state.showThinkingAfterToolCall = false;
    if (state.receivingEventsTimeout) {
      clearTimeout(state.receivingEventsTimeout);
      state.receivingEventsTimeout = null;
    }
    if (state.toolCallThinkingTimeout) {
      clearTimeout(state.toolCallThinkingTimeout);
      state.toolCallThinkingTimeout = null;
    }
    state.abortController = null;
    state.onStreamCompleteCallback = null;
    this.streams.delete(chatId);
  }

  markReceivingEvents(chatId: string) {
    const state = this.getOrCreate(chatId);
    state.isReceivingEvents = true;
    state.showThinkingAfterToolCall = false;

    if (state.receivingEventsTimeout) {
      clearTimeout(state.receivingEventsTimeout);
    }
    if (state.toolCallThinkingTimeout) {
      clearTimeout(state.toolCallThinkingTimeout);
      state.toolCallThinkingTimeout = null;
    }

    state.receivingEventsTimeout = setTimeout(() => {
      runInAction(() => {
        const current = this.streams.get(chatId);
        if (current) {
          current.isReceivingEvents = false;
        }
      });
    }, RECEIVING_EVENTS_IDLE_MS);
  }

  markToolCallSettled(chatId: string) {
    const state = this.getOrCreate(chatId);
    state.showThinkingAfterToolCall = false;

    if (state.toolCallThinkingTimeout) {
      clearTimeout(state.toolCallThinkingTimeout);
    }

    state.toolCallThinkingTimeout = setTimeout(() => {
      runInAction(() => {
        const current = this.streams.get(chatId);
        if (current) {
          current.showThinkingAfterToolCall = true;
          current.toolCallThinkingTimeout = null;
        }
      });
    }, TOOL_CALL_THINKING_IDLE_MS);
  }

  markStreamingStarted(chatId: string) {
    const state = this.getOrCreate(chatId);
    if (!state.hasStreamingStarted) {
      state.hasStreamingStarted = true;
    }
  }

  abort(chatId: string) {
    const state = this.streams.get(chatId);
    if (state?.abortController) {
      state.abortController.abort();
    }
    this.cleanup(chatId);
  }

  triggerStreamComplete(chatId: string) {
    const state = this.streams.get(chatId);
    if (state?.onStreamCompleteCallback) {
      state.onStreamCompleteCallback();
    }
  }

  sendMessage = flow(function* sendMessageGenerator(
    this: StreamStore,
    chatId: string,
    data: VibeChatRequest,
    handlers: SendMessageHandlers,
  ) {
    const { onEvent, onNonStreamingResponse, onError, onStreamComplete } = handlers;
    if (data.workspace_id) {
      this.currentWorkspaceId = data.workspace_id;
    }

    const state = this.getOrCreate(chatId);
    state.isLoading = true;
    state.hasStreamingStarted = false;
    state.isReceivingEvents = false;
    state.showThinkingAfterToolCall = false;
    if (state.receivingEventsTimeout) {
      clearTimeout(state.receivingEventsTimeout);
      state.receivingEventsTimeout = null;
    }
    if (state.toolCallThinkingTimeout) {
      clearTimeout(state.toolCallThinkingTimeout);
      state.toolCallThinkingTimeout = null;
    }

    state.abortController = new AbortController();
    state.onStreamCompleteCallback = onStreamComplete || null;

    try {
      const url = `${ENDPOINTS.vibe_chat}?stream=true`;
      const response: Response = yield fetch(url, {
        method: "POST",
        headers: jsonAuthHeaders(this.authToken!),
        body: JSON.stringify(data),
        signal: state.abortController.signal,
      });

      if (!response.ok) {
        const errorText = yield response.text();
        let errorMessage = `HTTP error! status: ${response.status}`;
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.message || errorMessage;
        } catch {
          errorMessage = errorText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("text/event-stream")) {
        const result = yield response.json();
        if (result.success && onNonStreamingResponse) {
          onNonStreamingResponse(result);
        }
        this.cleanup(chatId);
        return;
      }

      if (!response.body) {
        throw new Error("Response body is null");
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let isDone = false;

      try {
        while (!isDone) {
          const result: ReadableStreamReadResult<Uint8Array> = yield reader.read();
          const { done, value } = result;

          if (done) {
            this.cleanup(chatId);
            isDone = true;
            break;
          }

          buffer += decoder.decode(value, { stream: true });
          buffer = parseSSEEventsFromBuffer(buffer, onEvent);
        }
      } finally {
        reader.releaseLock();
      }
    } catch (error: unknown) {
      const err = error as { name?: string; message?: string };
      if (err.name === "AbortError") {
        this.cleanup(chatId);
      } else {
        this.cleanup(chatId);
        if (onError) {
          onError(new Error(err.message || "Failed to get response"), -1, null);
        }
      }
      throw error;
    }
  });
}
